// audio/wire-engine.js: M5 Change #3 / refactored in #9.
//
// Subscribes the events.js bus to the audio engine. Game code stays on
// emit(eventName) (the M2 contract); this file is the bridge that fires
// the right sample for each event.
//
// M5 #9: the dispatch table moved into audio-events.js so designers
// adjusting volume/pitch for a single sample don't touch wiring code.
// This module is now a thin loop over AUDIO_MAP, adding a new event
// → sample mapping requires zero changes here.

import { subscribe } from './events.js';
import { AUDIO_MAP } from './audio-events.js';

/**
 * Returns the unsubscribe function (calls all underlying unsubs).
 * @param {ReturnType<typeof import('./engine.js').createAudioEngine>} engine
 * @returns {() => void}
 */
export function wireAudioEngine(engine) {
  const unsubs = [];
  for (const entry of AUDIO_MAP) {
    const { event, sample, playOpts } = entry;
    unsubs.push(subscribe(event, (payload) => {
      // M5 #11: per-emit playbackRate/volume override. Lets score_tick
      // emit `{ playbackRate: 1.0 + pos * 0.15 }` for the pentatonic
      // pitch-rise across digits without each subscription site needing
      // its own engine binding.
      const opts = mergePlayOpts(playOpts, payload);
      engine.play(sample, opts);
    }));
  }
  return () => { for (const u of unsubs) u(); };
}

/**
 * Merge declarative playOpts with an emit payload's optional overrides.
 * Only the fields engine.play() reads (playbackRate, volume) are
 * merged, extraneous payload fields stay metadata.
 *
 * @param {{ playbackRate?: number, volume?: number }} base
 * @param {any} payload
 */
function mergePlayOpts(base, payload) {
  if (!payload || typeof payload !== 'object') return base;
  const out = { ...base };
  if (typeof payload.playbackRate === 'number') out.playbackRate = payload.playbackRate;
  if (typeof payload.volume === 'number')       out.volume       = payload.volume;
  return out;
}
